import {
  Box,
  Text,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Button,
  useToast,
  VStack,
  Heading,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {BsChevronDown} from "react-icons/bs";
import {HiOutlineLocationMarker} from "react-icons/hi";
import {useState, useMemo} from "react";
import {crud} from "../../crudRequests";

export default function Locations({state, dispatch}) {
  const patient = state.patient;
  const [patientLid, setPatientLid] = useState(patient.lid || []);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const allLocations = state.locations || [];

  const patientLocationIds = useMemo(() => {
    let locSet = new Set(allLocations.map((loc) => loc.lid));
    if (!patientLid) return [];
    if (Array.isArray(patientLid))
      return patientLid.filter((lid) => locSet.has(lid));
    return locSet.has(patientLid) ? [patientLid] : [];
  }, [patientLid, allLocations]);

  const assignedLocations = useMemo(() => {
    return allLocations.filter((loc) => patientLocationIds.includes(loc.lid));
  }, [allLocations, patientLocationIds]);

  const handleToggleLocation = (locationId) => {
    if (patientLocationIds.includes(locationId)) {
      setPatientLid(patientLocationIds.filter((id) => id !== locationId));
    } else {
      setPatientLid([...patientLocationIds, locationId]);
    }
  };

  const handleRemoveLocation = (locationId) => {
    setPatientLid(patientLocationIds.filter((id) => id !== locationId));
  };

  const allSelected =
    allLocations.length > 0 &&
    patientLocationIds.length === allLocations.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setPatientLid([]);
    } else {
      setPatientLid(allLocations.map((loc) => loc.lid));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await crud(state, [
        {
          db: state.db,
          collection: "patients",
          parameters: [{pid: patient.pid}, {$set: {lid: patientLocationIds}}],
          method: "updateOne",
        },
      ]);

      dispatch({
        type: "UPDATE_PATIENT",
        payload: {...patient, lid: patientLocationIds},
      });

      toast({
        title: "Locations Updated",
        description: "Your locations have been successfully updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed to update locations",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box w="full" px={0} py={2}>
      <Box
        bg="gray.50"
        borderRadius="12px"
        px={{base: 4, md: 6}}
        py={{base: 4, md: 6}}
        mb={4}
      >
        <VStack spacing={4} align="stretch">
          <Flex align="center" gap={2}>
            <Icon as={HiOutlineLocationMarker} boxSize={5} color="gray.600" />
            <Heading as="h3" size="sm" color="gray.800">
              My Locations
            </Heading>
          </Flex>

          <Text fontSize="sm" color="gray.500">
            Manage the locations you belong to. Select from available locations
            below.
          </Text>

          <Box>
            <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
              Select Locations
            </Text>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Box}
                cursor="pointer"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={3}
                py={2}
                bg="white"
                w="100%"
                _hover={{borderColor: "gray.300"}}
              >
                <Flex justify="space-between" align="center">
                  <Text
                    fontSize="sm"
                    color={patientLocationIds.length ? "gray.800" : "gray.500"}
                  >
                    {patientLocationIds.length
                      ? `${patientLocationIds.length} location${patientLocationIds.length > 1 ? "s" : ""} selected`
                      : "Select locations..."}
                  </Text>
                  <BsChevronDown size={12} color="gray" />
                </Flex>
              </MenuButton>
              <MenuList maxH="250px" overflowY="auto" py={1} minW="300px">
                {allLocations.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" px={3} py={2}>
                    No locations available
                  </Text>
                ) : (
                  <>
                    <Box px={3} py={2} _hover={{bg: "gray.50"}}>
                      <Checkbox
                        isChecked={allSelected}
                        isIndeterminate={
                          patientLocationIds.length > 0 && !allSelected
                        }
                        onChange={handleSelectAll}
                        size="sm"
                        colorScheme="blue"
                      >
                        <Text fontSize="sm" fontWeight="600">
                          Select All
                        </Text>
                      </Checkbox>
                    </Box>
                    <Divider my={1} />
                    {allLocations.map((location) => (
                      <Box
                        key={location.lid}
                        px={3}
                        py={2}
                        _hover={{bg: "gray.50"}}
                      >
                        <Checkbox
                          isChecked={patientLocationIds.includes(location.lid)}
                          onChange={() => handleToggleLocation(location.lid)}
                          size="sm"
                          colorScheme="blue"
                        >
                          <Box>
                            <Text fontSize="sm" fontWeight="500">
                              {location.name}
                            </Text>
                            {location.address && (
                              <Text fontSize="xs" color="gray.500">
                                {location.address}
                              </Text>
                            )}
                          </Box>
                        </Checkbox>
                      </Box>
                    ))}
                  </>
                )}
              </MenuList>
            </Menu>
          </Box>

          {assignedLocations.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">
                Assigned Locations
              </Text>
              <Wrap spacing={2}>
                {assignedLocations.map((location) => (
                  <WrapItem key={location.lid}>
                    <Tag
                      size="md"
                      variant="subtle"
                      colorScheme="blue"
                      borderRadius="full"
                      py={1}
                      px={3}
                    >
                      <Icon as={HiOutlineLocationMarker} mr={1} />
                      <TagLabel>{location.name}</TagLabel>
                      <TagCloseButton
                        onClick={() => handleRemoveLocation(location.lid)}
                      />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {assignedLocations.length === 0 && (
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px dashed"
              borderColor="gray.200"
            >
              <Text fontSize="sm" color="gray.500" textAlign="center">
                No locations assigned
              </Text>
            </Box>
          )}
        </VStack>
      </Box>

      <Button
        variant="outline"
        borderColor="gray.300"
        bg="white"
        _hover={{bg: "#f8f9fa"}}
        isLoading={isLoading}
        onClick={handleSave}
        px="8"
        size="sm"
        display="block"
        mx="auto"
        my={3}
      >
        Save
      </Button>
    </Box>
  );
}
